import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  layoutTypes,
  leftSidebarTypes,
  layoutModeTypes,
  layoutWidthTypes,
  layoutPositionTypes,
  topbarThemeTypes,
  leftsidbarSizeTypes,
  leftSidebarViewTypes,
  leftSidebarImageTypes,
  preloaderTypes,
} from "../../components/constants/layout";

interface LayoutState {
  layoutType: string;
  leftSidebarType: string;
  layoutModeType: string;
  layoutWidthType: string;
  layoutPositionType: string;
  topbarThemeType: string;
  leftsidbarSizeType: string;
  leftSidebarViewType: string;
  leftSidebarImageType: string;
  preloader: string;
}

const data = localStorage.getItem("layout");
let defaultState: LayoutState = {
  layoutType: layoutTypes.VERTICAL,
  leftSidebarType: leftSidebarTypes.DARK,
  layoutModeType: layoutModeTypes.LIGHTMODE,
  layoutWidthType: layoutWidthTypes.FLUID,
  layoutPositionType: layoutPositionTypes.FIXED,
  topbarThemeType: topbarThemeTypes.LIGHT,
  leftsidbarSizeType: leftsidbarSizeTypes.DEFAULT,
  leftSidebarViewType: leftSidebarViewTypes.DEFAULT,
  leftSidebarImageType: leftSidebarImageTypes.NONE,
  preloader: preloaderTypes.DISABLE,
};
if (data) {
  defaultState = JSON.parse(data);
}

function changeHTMLAttribute(attribute: string, value: string) {
  if (document.documentElement) {
    document.documentElement.setAttribute(attribute, value);
  }
}

const persistAndReturn = (state: LayoutState) => {
  localStorage.setItem("layout", JSON.stringify(state));
};

const layoutSlice = createSlice({
  name: "Layout",
  initialState: defaultState,
  reducers: {
    changeLayout(state, action: PayloadAction<string>) {
      state.layoutType = action.payload;
      if (action.payload === "twocolumn") {
        document.documentElement.removeAttribute("data-layout-width");
      } else if (action.payload === "horizontal") {
        document.documentElement.removeAttribute("data-sidebar-size");
      }
      changeHTMLAttribute("data-layout", action.payload);
      persistAndReturn(state);
    },
    changeLayoutMode(state, action: PayloadAction<string>) {
      state.layoutModeType = action.payload;
      changeHTMLAttribute("data-layout-mode", action.payload);
      persistAndReturn(state);
    },
    changeSidebarTheme(state, action: PayloadAction<string>) {
      state.leftSidebarType = action.payload;
      changeHTMLAttribute("data-sidebar", action.payload);
      persistAndReturn(state);
    },
    changeLayoutWidth(state, action: PayloadAction<string>) {
      state.layoutWidthType = action.payload;
      if (action.payload === "lg") {
        changeHTMLAttribute("data-layout-width", "fluid");
      } else {
        changeHTMLAttribute("data-layout-width", "boxed");
      }
      persistAndReturn(state);
    },
    changeLayoutPosition(state, action: PayloadAction<string>) {
      state.layoutPositionType = action.payload;
      changeHTMLAttribute("data-layout-position", action.payload);
      persistAndReturn(state);
    },
    changeTopbarTheme(state, action: PayloadAction<string>) {
      state.topbarThemeType = action.payload;
      changeHTMLAttribute("data-topbar", action.payload);
      persistAndReturn(state);
    },
    changeLeftsidebarSizeType(state, action: PayloadAction<string>) {
      state.leftsidbarSizeType = action.payload;
      switch (action.payload) {
        case "lg":
          changeHTMLAttribute("data-sidebar-size", "lg");
          break;
        case "md":
          changeHTMLAttribute("data-sidebar-size", "md");
          break;
        case "sm":
          changeHTMLAttribute("data-sidebar-size", "sm");
          break;
        case "sm-hover":
          changeHTMLAttribute("data-sidebar-size", "sm-hover");
          break;
        default:
          changeHTMLAttribute("data-sidebar-size", "lg");
      }
      persistAndReturn(state);
    },
    changeLeftsidebarViewType(state, action: PayloadAction<string>) {
      state.leftSidebarViewType = action.payload;
      changeHTMLAttribute("data-layout-style", action.payload);
      persistAndReturn(state);
    },
    changeSidebarImageType(state, action: PayloadAction<string>) {
      state.leftSidebarImageType = action.payload;
      changeHTMLAttribute("data-sidebar-image", action.payload);
      persistAndReturn(state);
    },
    resetValue(state) {
      Object.assign(state, defaultState);
      persistAndReturn(state);
    },
    changePreLoader(state, action: PayloadAction<string>) {
      state.preloader = action.payload;
      changeHTMLAttribute("data-preloader", action.payload);
      persistAndReturn(state);
    },
  },
});

export const {
  changeLayout,
  changeLayoutMode,
  changeSidebarTheme,
  changeLayoutWidth,
  changeLayoutPosition,
  changeTopbarTheme,
  changeLeftsidebarSizeType,
  changeLeftsidebarViewType,
  changeSidebarImageType,
  resetValue,
  changePreLoader,
} = layoutSlice.actions;
export default layoutSlice.reducer;
